import { Alert, Spin } from 'antd';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken, setAuthToken } from 'features/auth/helpers';
import { authQueryApi, useGetCurrentUserQuery } from 'features/auth/query';
import { clearAuth, setCurrentUser } from 'features/auth/slice';
import { USE_SSO } from 'lib/constants';
import { QueryTags } from 'lib/rtkQuery/constants';
import { useAppDispatch } from 'lib/stores/hook';
import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';

const KeycloakAuthentication = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const [authState, setAuthState] = useState({
    hasTriedSignin: false,
    isAuthReady: false,
    isTokenReady: false,
  });

  const refs = {
    lastUserSub: useRef<string | null>(null),
    isProcessing: useRef(false),
    authCheckTimeout: useRef<NodeJS.Timeout>(),
  };

  const {
    isAuthenticated,
    activeNavigator,
    isLoading,
    user,
    error,
    signinRedirect,
  } = useAuth();

  const { data: currentUser, refetch: refetchCurrentUser } =
    useGetCurrentUserQuery(undefined, {
      skip: !isAuthenticated || !user?.access_token || !authState.isTokenReady,
    });

  const handleUserInformation = useCallback(() => {
    if (!user || refs.isProcessing.current) return;

    const token = user.access_token;
    const expiresAt = user.expires_at;
    const expiresInMs =
      expiresAt && typeof expiresAt === 'number'
        ? expiresAt * 1000
        : expiresAt
          ? new Date(expiresAt).getTime()
          : undefined;

    setAuthToken(TOKEN.ACCESS_TOKEN, token, expiresInMs);

    const currentUserSub = user.profile?.sub;
    if (refs.lastUserSub.current !== currentUserSub) {
      refs.isProcessing.current = true;
      refs.lastUserSub.current = currentUserSub || null;

      // Reset user data
      dispatch(setCurrentUser(null));
      dispatch(authQueryApi.util.invalidateTags([QueryTags.UserDetail]));

      // Give time for state updates to propagate
      setTimeout(() => {
        setAuthState(prev => ({ ...prev, isTokenReady: true }));
        refs.isProcessing.current = false;
      }, 100);
    }
  }, [user, refs.isProcessing, refs.lastUserSub, dispatch]);

  // Fetch user data when token is ready
  useEffect(() => {
    if (
      authState.isTokenReady &&
      isAuthenticated &&
      user?.access_token &&
      !currentUser
    ) {
      refetchCurrentUser();
    }
  }, [
    authState.isTokenReady,
    isAuthenticated,
    user?.access_token,
    currentUser,
    refetchCurrentUser,
  ]);

  // Handle authentication check
  const handleAuthenticationCheck = useCallback(() => {
    if (refs.authCheckTimeout.current) {
      clearTimeout(refs.authCheckTimeout.current);
    }

    const routeHasAuthParams = hasAuthParams();

    if (
      !isLoading &&
      !isAuthenticated &&
      !activeNavigator &&
      !authState.hasTriedSignin &&
      !routeHasAuthParams
    ) {
      // Redirect to sign in page
      void signinRedirect();
      setAuthState(prev => ({ ...prev, hasTriedSignin: true }));
    } else if (isAuthenticated && user?.access_token) {
      // Set auth as ready after a small delay
      refs.authCheckTimeout.current = setTimeout(() => {
        setAuthState(prev => ({ ...prev, isAuthReady: true }));
      }, 100);
    }
  }, [
    refs.authCheckTimeout,
    isLoading,
    isAuthenticated,
    activeNavigator,
    authState.hasTriedSignin,
    user?.access_token,
    signinRedirect,
  ]);

  // Initialize authentication check
  useEffect(() => {
    handleAuthenticationCheck();
    return () => {
      if (refs.authCheckTimeout.current) {
        clearTimeout(refs.authCheckTimeout.current);
      }
    };
  }, [handleAuthenticationCheck, refs.authCheckTimeout]);

  // Process user information when auth is ready
  useEffect(() => {
    if (authState.isAuthReady && user?.access_token) {
      handleUserInformation();
    }
  }, [handleUserInformation, authState.isAuthReady, user?.access_token]);

  // Handle logout or authentication failure
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      // Clean up state when not authenticated
      dispatch(clearAuth());
      dispatch(authQueryApi.util.invalidateTags([QueryTags.UserDetail]));
      dispatch(authQueryApi.util.resetApiState());

      // Reset internal state
      refs.lastUserSub.current = null;
      refs.isProcessing.current = false;
      setAuthState({
        hasTriedSignin: false,
        isAuthReady: false,
        isTokenReady: false,
      });
    }
  }, [
    isAuthenticated,
    isLoading,
    dispatch,
    refs.lastUserSub,
    refs.isProcessing,
  ]);

  useEffect(() => {
    if (!USE_SSO) {
      const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);
      if (accessToken) {
        setAuthToken(TOKEN.ACCESS_TOKEN, accessToken);
      }
    }
  }, [dispatch]);

  if (isLoading || !authState.isAuthReady) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <Spin size="large" />
        <p className="mt-5 text-base font-bold">Đang xác thực...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center px-5">
        <h1 className="text-red-700">Không thể đăng nhập</h1>
        <Alert
          type="error"
          showIcon
          message="Sign in failed"
          description={
            error?.message ||
            'Không thể đăng nhập. Vui lòng thử lại hoặc liên hệ với bộ phận hỗ trợ.!!'
          }
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default memo(KeycloakAuthentication);
